/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   JU : รับสินค้าเข้าสต๊อกโดยตรง (ไม่ผ่านใบสั่งซื้อ)

   เพิ่มสินค้าจาก master (RM / IG / Packaging / Packing / FG)
   สร้าง → "รอยืนยัน" แก้ได้ → "ยืนยันรับเข้าสต๊อก" เข้า er_stock เป็น lot
   เลข lot = เลขที่ JU-ลำดับ  พิมพ์ Tags ได้ด้วยฟอร์มเดียวกับ GR
   (แบ่งป้ายตามจำนวนกล่องของแต่ละรายการ)
============================================================ */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card, Form, Flex, Row, Col, Space, Input, InputNumber, Button, Table,
  Typography, DatePicker, Select, Modal, Popconfirm, Tag, message,
} from "antd";
import {
  ArrowLeftOutlined, SaveFilled, CheckCircleOutlined, StopOutlined, PlusOutlined,
} from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import dayjs from "dayjs";
import ModalItemPicker from "../../../components/modal/ERP/item-picker/ModalItemPicker";

import JuService from "../../../service/ERP/Ju.service";
import useGoBack from "../../../hooks/useGoBack";
import { JU_PENDING, JU_CONFIRMED, JU_CANCELLED, StatusTag, num, fdate, printTags } from "./model";
import ActionGroup, { PrintButton } from "../../../components/action-group/ActionGroup";

const juService = JuService();

/* ปุ่มหัวหน้าขนาดเดียวกับหน้าเอกสารอื่นของระบบ */
const ACTION_BTN = { minWidth: "9.5rem", height: 36, paddingInline: 12 };
const errMsg = (err, d) => err?.response?.data?.message || d;
const toDay = (v) => (v ? dayjs(v) : null);
const ymd = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

const TYPE_LABEL = { RM: "Raw Material", IG: "Ingredients", Packaging: "Packaging", Packing: "Packing", FG: "Finished Goods" };

export default function JuManage() {
  const location = useLocation();
  const goBack = useGoBack("/ERP/ju");
  const config = location.state?.config || { action: "create" };

  const [form] = Form.useForm();
  const [header, setHeader] = useState({});
  const [items, setItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [openPicker, setOpenPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const isCreate = config.action === "create";
  const status = header.doc_status || JU_PENDING;
  const editable = config.action !== "view" && status === JU_PENDING;

  const withKeys = (rows) => rows.map((r, i) => ({ ...r, _key: `${r.stcode}-${i}-${Date.now()}` }));

  const applyLoaded = (data) => {
    const h = data?.header || {};
    setHeader(h);
    setItems(withKeys(data?.items || []));
    form.setFieldsValue({ judate: h.judate ? dayjs(h.judate) : dayjs(), remark: h.remark });
  };

  useEffect(() => {
    if (!isCreate && config.code) {
      juService
        .get(config.code)
        .then((res) => applyLoaded(res.data?.data))
        .catch((err) => message.error(errMsg(err, "โหลดเอกสารไม่สำเร็จ")));
    } else {
      form.setFieldsValue({ judate: dayjs() });
    }
  }, []);

  const searchItems = (q) =>
    juService
      .items(q || "")
      .then((res) =>
        setItemOptions(
          (res.data?.data || []).map((m) => ({
            value: m.stcode,
            label: `${m.stcode} — ${m.stname} (${TYPE_LABEL[m.typename] || m.typename})`,
            raw: m,
          }))
        )
      )
      .catch(() => {});

  /* สินค้าเดียวกันเพิ่มได้หลายแถว (คนละวันผลิต = คนละ lot) */
  const addItem = (_v, opt) => {
    const m = opt?.raw || {};
    setItems((prev) => [
      ...prev,
      {
        _key: `${m.stcode}-${Date.now()}`,
        stcode: m.stcode,
        stname: m.stname,
        typename: m.typename,
        unit: m.unit,
        shelf_life: m.shelf_life,
        qty: 0,
        price: 0,
        box_count: 1,
        mfg_date: null,
        expire_date: null,
      },
    ]);
  };

  const updateItem = (key, patch) =>
    setItems((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const onMfgChange = (r, d) => {
    const patch = { mfg_date: ymd(d) };
    if (d && Number(r.shelf_life || 0) > 0) {
      patch.expire_date = ymd(dayjs(d).add(Number(r.shelf_life), "day"));
    }
    updateItem(r._key, patch);
  };

  const payload = () => {
    const v = form.getFieldsValue();
    return {
      jucode: header.jucode,
      judate: ymd(v.judate),
      remark: v.remark || null,
      items: items.map((r) => ({
        stcode: r.stcode,
        stname: r.stname,
        typename: r.typename,
        unit: r.unit,
        qty: Number(r.qty || 0),
        price: Number(r.price || 0),
        box_count: Number(r.box_count || 1),
        mfg_date: r.mfg_date || null,
        expire_date: r.expire_date || null,
        remark: r.remark || null,
      })),
    };
  };

  const validate = (forConfirm) => {
    if (items.length === 0) return "ยังไม่มีรายการสินค้า";
    if (forConfirm) {
      const z = items.find((r) => !(Number(r.qty || 0) > 0));
      if (z) return `${z.stcode} จำนวนต้องมากกว่า 0`;
      const e = items.find((r) => !r.expire_date);
      if (e) return `${e.stcode} ยังไม่ระบุวันหมดอายุ`;
    }
    return null;
  };

  const handleSave = () => {
    const e = validate(false);
    if (e) return message.warning(e);
    setSaving(true);
    (header.jucode ? juService.update(payload()) : juService.create(payload()))
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("บันทึกแล้ว (รอยืนยันรับเข้าสต๊อก)");
      })
      .catch((err) => message.error(errMsg(err, "บันทึกไม่สำเร็จ")))
      .finally(() => setSaving(false));
  };

  const handleConfirm = async () => {
    const e = validate(true);
    if (e) return message.warning(e);
    setSaving(true);
    try {
      let jucode = header.jucode;
      if (!jucode) {
        const res = await juService.create(payload());
        applyLoaded(res.data?.data);
        jucode = res.data?.data?.header?.jucode;
      }
      const res = await juService.confirm({ ...payload(), jucode });
      applyLoaded(res.data?.data);
      message.success("รับสินค้าเข้าสต๊อกเรียบร้อย");
    } catch (err) {
      Modal.error({
        title: "รับเข้าสต๊อกไม่สำเร็จ",
        content: <div style={{ whiteSpace: "pre-line" }}>{errMsg(err, "เกิดข้อผิดพลาด")}</div>,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDoc = () =>
    juService
      .cancel(header.jucode)
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("ยกเลิกเอกสารแล้ว");
      })
      .catch((err) =>
        Modal.error({
          title: "ยกเลิกไม่สำเร็จ",
          content: <div style={{ whiteSpace: "pre-line" }}>{errMsg(err, "เกิดข้อผิดพลาด")}</div>,
        })
      );

  const totalValue = useMemo(
    () => items.reduce((t, r) => t + Number(r.qty || 0) * Number(r.price || 0), 0),
    [items]
  );

  const numInput = (r, field, props = {}) =>
    editable ? (
      <InputNumber
        size="small"
        style={{ width: "100%" }}
        value={Number(r[field] || 0)}
        onChange={(val) => updateItem(r._key, { [field]: Number(val || 0) })}
        {...props}
      />
    ) : (
      num(r[field])
    );

  const columns = [
    { title: "ลำดับ", key: "no", width: 60, align: "center", render: (_, __, i) => i + 1 },
    { title: "รหัสสินค้า", dataIndex: "stcode", key: "stcode", width: 130 },
    { title: "ชื่อสินค้า", dataIndex: "stname", key: "stname" },
    { title: "ประเภท", dataIndex: "typename", key: "typename", width: 110, render: (v) => TYPE_LABEL[v] || v || "-" },
    { title: "จำนวน", key: "qty", width: 120, align: "right", render: (_, r) => numInput(r, "qty", { min: 0, step: 0.001 }) },
    { title: "หน่วย", dataIndex: "unit", key: "unit", width: 70, align: "center" },
    { title: "ราคา/หน่วย", key: "price", width: 110, align: "right", render: (_, r) => numInput(r, "price", { min: 0, step: 0.01 }) },
    { title: "จำนวนกล่อง (Tags)", key: "box_count", width: 120, align: "right", render: (_, r) => numInput(r, "box_count", { min: 1, step: 1, precision: 0 }) },
    {
      title: "วันผลิต",
      key: "mfg_date",
      width: 140,
      render: (_, r) =>
        editable ? (
          <DatePicker size="small" style={{ width: "100%" }} value={toDay(r.mfg_date)} onChange={(d) => onMfgChange(r, d)} />
        ) : (
          fdate(r.mfg_date)
        ),
    },
    {
      title: "วันหมดอายุ",
      key: "expire_date",
      width: 140,
      render: (_, r) =>
        editable ? (
          <DatePicker size="small" style={{ width: "100%" }} value={toDay(r.expire_date)} onChange={(d) => updateItem(r._key, { expire_date: ymd(d) })} />
        ) : (
          fdate(r.expire_date)
        ),
    },
    ...(header.jucode ? [{ title: "Lot No.", dataIndex: "lot_no", key: "lot_no", width: 140 }] : []),
    ...(editable
      ? [{
          title: "",
          key: "del",
          width: 50,
          align: "center",
          render: (_, r) => (
            <Button type="text" danger size="small" icon={<RiDeleteBin5Line />}
              onClick={() => setItems((prev) => prev.filter((x) => x._key !== r._key))} />
          ),
        }]
      : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Space align="center">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config.title || "JU รับสินค้าเข้าสต๊อก"} {header.jucode ? `: ${header.jucode}` : ""}
          </Typography.Title>
          {header.jucode && <StatusTag value={status} />}
        </Space>
        <Space wrap>
          <Button style={ACTION_BTN} className="bn-center justify-center" icon={<ArrowLeftOutlined />} onClick={goBack}>
            ย้อนกลับ
          </Button>
          <ActionGroup type="print">
            {header.jucode && status !== JU_CANCELLED && (
              <PrintButton onClick={() => printTags(header.jucode)}>พิมพ์ Tags</PrintButton>
            )}
          </ActionGroup>
          <ActionGroup type="action">
            {editable && (
              <Popconfirm
                placement="bottomRight"
                title="ยืนยันรับสินค้าเข้าสต๊อก?"
                description="สินค้าจะเข้าสต๊อกตามจำนวน หลังยืนยันแล้วแก้ไขรายการไม่ได้"
                okText="ยืนยันรับเข้าสต๊อก"
                cancelText="ยังก่อน"
                onConfirm={handleConfirm}
              >
                <Button style={{ ...ACTION_BTN, borderColor: "#389e0d", color: "#fff", background: "#389e0d" }} className="bn-center justify-center" icon={<CheckCircleOutlined />} loading={saving}>
                  ยืนยันรับเข้าสต๊อก
                </Button>
              </Popconfirm>
            )}
            {header.jucode && status !== JU_CANCELLED && config.action !== "view" && (
              <Popconfirm
                placement="bottomRight"
                title="ยกเลิกเอกสารนี้?"
                description={status === JU_CONFIRMED ? "ระบบจะถอนสินค้าออกจากสต๊อก (ถ้ายังไม่ถูกเบิกใช้)" : undefined}
                okText="ยกเลิกเอกสาร"
                cancelText="ไม่"
                onConfirm={handleCancelDoc}
              >
                <Button danger style={ACTION_BTN} className="bn-center justify-center" icon={<StopOutlined />}>
                  ยกเลิกเอกสาร
                </Button>
              </Popconfirm>
            )}
          </ActionGroup>
          {editable && (
            <Button type="primary" style={ACTION_BTN} className="bn-center justify-center" icon={<SaveFilled />} loading={saving} onClick={handleSave}>
              บันทึก (รอยืนยัน)
            </Button>
          )}
        </Space>
      </Flex>

      <Card size="small">
        <Form form={form} layout="vertical" disabled={!editable} autoComplete="off">
          <Row gutter={[12, 0]}>
            <Col xs={24} md={6}>
              <Form.Item label="วันที่รับเข้า" name="judate">
                <DatePicker size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item label="หมายเหตุ / ที่มาของสินค้า" name="remark">
                <Input size="large" placeholder="เช่น ยอดยกมา, ตรวจนับเกิน, รับคืน" />
              </Form.Item>
            </Col>
          </Row>
          {header.confirmed_date && (
            <Tag color="green">
              รับเข้าสต๊อกเมื่อ {dayjs(header.confirmed_date).format("DD/MM/YYYY HH:mm")}
            </Tag>
          )}
        </Form>
      </Card>

      <Card
        size="small"
        title="รายการสินค้า"
        extra={
          editable && (
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpenPicker(true)}>
              เพิ่มสินค้า
            </Button>
          )
        }
      >
        <Table
          size="small"
          rowKey="_key"
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "เพิ่มสินค้าจากช่องค้นหาด้านบน" }}
          footer={() =>
            items.length > 0 && (
              <Flex justify="end">
                <Typography.Text>
                  มูลค่ารวม <b>{num(totalValue, 2)}</b> บาท
                </Typography.Text>
              </Flex>
            )
          }
        />
      </Card>
      <ModalItemPicker
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        title="เลือกสินค้า"
        fetchItems={() => juService.items("", true).then((res) => res.data?.data || [])}
        typeLabel={TYPE_LABEL}
        onSelect={(r) => addItem(r.stcode, { raw: r })}
      />
    </Space>
  );
}
